import React from 'react';
import { Button, Checkbox } from '@mantine/core';
import classes from './pos-limit-items-section.module.scss';

const INFINITY_SYMBOL = '∞';

export type LimitItemRowProps = {
  id: number;
  name: string;
  limit: number | null;
  isSelected: boolean;
  isManager: boolean;
  interactionsLocked: boolean;
  onOpenNumPad: (productId: number) => void;
  onToggleSelected: (productId: number, checked: boolean) => void;
  onClearLimit: (productId: number) => void;
};

// Architectural summary: memoized limit-items row; stable props skip whole-list re-renders.
const LimitItemRowComponent: React.FC<LimitItemRowProps> = ({
  id,
  name,
  limit,
  isSelected,
  isManager,
  interactionsLocked,
  onOpenNumPad,
  onToggleSelected,
  onClearLimit,
}) => {
  const hasLimit = limit !== null;
  const isOutOfStock = limit === 0;
  const isLimited = limit !== null && limit > 0;
  const rowInteractive = isManager && !interactionsLocked;

  return (
    <div
      data-testid={`limit-items-row-${id}`}
      className={`${classes.row} ${!isManager ? classes.rowReadOnly : ''}`}
      role={rowInteractive ? 'button' : undefined}
      tabIndex={rowInteractive ? 0 : undefined}
      onClick={rowInteractive ? () => onOpenNumPad(id) : undefined}
      onKeyDown={
        rowInteractive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpenNumPad(id);
              }
            }
          : undefined
      }
    >
      <div className={classes.productCell}>
        {isManager && (
          <span className={classes.productCheckbox} onClick={(event) => event.stopPropagation()}>
            <Checkbox
              data-testid={`limit-items-select-${id}`}
              checked={isSelected}
              onChange={(event) => onToggleSelected(id, event.currentTarget.checked)}
              aria-label={`בחר ${name}`}
              disabled={interactionsLocked}
            />
          </span>
        )}
        <span className={classes.productName}>{name}</span>
      </div>

      <div className={classes.qtyCell}>
        <span
          className={`${classes.qtyBadge} ${isOutOfStock ? classes.qtyBadgeDanger : ''} ${isLimited ? classes.qtyBadgeInfo : ''}`}
          aria-label={isOutOfStock ? 'אזל מהמלאי' : isLimited ? `הגבלה ${limit}` : 'ללא הגבלה'}
        >
          {hasLimit ? limit : INFINITY_SYMBOL}
        </span>
        {isManager && (
          <Button
            type="button"
            data-testid={`limit-items-clear-${id}`}
            className={classes.clearButton}
            onClick={(event) => {
              event.stopPropagation();
              onClearLimit(id);
            }}
            disabled={interactionsLocked || !hasLimit}
            title={!hasLimit ? 'אין הגבלה לאיפוס' : undefined}
          >
            אפס הגבלה
          </Button>
        )}
      </div>
    </div>
  );
};

export const LimitItemRow = React.memo(LimitItemRowComponent);
